import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, withStyles, alpha } from '@material-ui/core/styles';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

/// Scheduler Testing
import { ViewState } from '@devexpress/dx-react-scheduler';
import { Scheduler } from '@devexpress/dx-react-scheduler-material-ui';
import { MonthView } from '@devexpress/dx-react-scheduler-material-ui';
import { Appointments } from '@devexpress/dx-react-scheduler-material-ui';
import { Toolbar } from '@devexpress/dx-react-scheduler-material-ui';
import { DateNavigator } from '@devexpress/dx-react-scheduler-material-ui';
import { ViewSwitcher } from '@devexpress/dx-react-scheduler-material-ui';
import { WeekView } from '@devexpress/dx-react-scheduler-material-ui';
import { DayView } from '@devexpress/dx-react-scheduler-material-ui';
import { AllDayPanel } from '@devexpress/dx-react-scheduler-material-ui';
import { TodayButton } from '@devexpress/dx-react-scheduler-material-ui';

import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import MenuList from '@material-ui/core/MenuList';
import Grow from '@material-ui/core/Grow';
import Tooltip from '@material-ui/core/Tooltip';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Chip from '@material-ui/core/Chip';
import Popper from '@material-ui/core/Popper';

import CalendarIcon from '@material-ui/icons/Today';
import ListIcon from '@material-ui/icons/List';
import AppleIcon from 'app/components/icons/Apple';
import GoogleIcon from 'app/components/icons/Google';
import Office365Icon from 'app/components/icons/Office365';
import OutlookIcon from 'app/components/icons/Outlook';
import OutlookComIcon from 'app/components/icons/OutlookCom';
import YahooIcon from 'app/components/icons/Yahoo';

import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

const getBorder = theme => (`1px solid ${lighten(alpha(theme.palette.divider, 1), 0.88)}`);

const DayScaleCell = props => (
    <MonthView.DayScaleCell {...props} style={{ textAlign: 'center', fontWeight: 'bold' }} />
);

const styles = theme => ({
    cell: {
        color: '#78909C!important',
        position: 'relative',
        userSelect: 'none',
        verticalAlign: 'top',
        padding: 0,
        height: 100,
        borderLeft: getBorder(theme),

        '&:first-child': {
            borderLeft: 'none',
        },
        '&:last-child': {
            paddingRight: 0,
        },
        'tr:last-child &': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: 'white',
        },
        '&:focus': {
            backgroundColor: alpha(theme.palette.secondary.main, 0.15),
            outline: 0,
        },
    },
    content: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        position: 'absolute',
        alignItems: 'center',
    },
    text: {
        padding: '0.5em',
        textAlign: 'center',
    },
    opacity: {
        opacity: '0.5',
    },
    appointment: {
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.light,
        },
        backgroundColor: theme.palette.secondary.main
    },
    apptContent: {
        '& > div > div': {
            whiteSpace: 'normal !important',
            lineHeight: 1.2,
            marginTop: 6,
        },
    },
    flexibleSpace: {
        flex: 'none',
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    tooltipContent: {
        padding: theme.spacing(3, 1),
        paddingTop: 0,
        backgroundColor: theme.palette.background.paper,
        boxSizing: 'border-box',
        width: '400px',
    },
    tooltipText: {
        ...theme.typography.body2,
        display: 'inline-block',
    },
    title: {
        ...theme.typography.h6,
        color: theme.palette.text.secondary,
        fontWeight: theme.typography.fontWeightBold,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    icon: {
        color: theme.palette.secondary.light,
        verticalAlign: 'middle',
    },
    circle: {
        width: theme.spacing(4.5),
        height: theme.spacing(4.5),
        verticalAlign: 'super',
    },
    textCenter: {
        textAlign: 'center',
    },
    dateAndTitle: {
        lineHeight: 1.1,
    },
    titleContainer: {
        paddingBottom: theme.spacing(2),
    },
    container: {
        paddingBottom: theme.spacing(1.5),
    },
});

const Appointment = ({ data, ...restProps }) => {

    const history = useHistory();
    const classes = useStyles();

    return (
        <Appointments.Appointment
            {...restProps}
            onClick={() => history.push(`/events/${data.id}`)}
            className={classes.appointment}
        />
    )
};

const AppointmentContent = withStyles(styles, { name: 'AppointmentContent' })(({ classes, ...restProps }) => (
  <Appointments.AppointmentContent {...restProps} className={classes.apptContent} />
));

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    toolbarToggle: {
        textAlign: 'right',
        flexGrow: 1,
        paddingRight: theme.spacing(1)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(2)
    },
    marginLeft: {
        marginLeft: theme.spacing(2)
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    appointment: {
        borderRadius: '4px',
        backgroundColor: theme.palette.secondary.main,

        '&:hover': {
            backgroundColor: theme.palette.secondary.light,
        },
    },
    list: {
        backgroundColor: theme.palette.grey[200],
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    listItem: {
        backgroundColor: '#ffffff',
        marginBottom: theme.spacing(1),
    },
}));

function ListItemLink(props) {
    const { app } = useApp();
    const { event, service } = props;
    return <ListItem button target="_blank" component="a" href={`https://calndr.link/d/event/?service=${service}&start=${event.starts_at}&end=${event.ends_at}&title=${event.name}&timezone=${app.club.timezone}&description=${event.short_description}&location=${event.location}`} {...props} />;
}

function AdvancedMenuItem(props) {
    const history = useHistory();
    const now = DateTime.local().toSeconds()
    const { event } = props
    const classes = useStyles();

    const [ addEventDialogOpen, setAddEventDialogOpen ] = useState(false);
    const eventAnchorRef = useRef(null);

    // Add Event Dialog ----------------------------------

    const handleAddEventToggle = event => {
        event.stopPropagation()
        setAddEventDialogOpen((prevOpen) => !prevOpen);
    };
    
    const handleAddEventClose = event => {
        if (eventAnchorRef.current && eventAnchorRef.current.contains(event.target)) {
            return;
        }
    
        setAddEventDialogOpen(false);
    };
    
    const handleListKeyDown = event => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setAddEventDialogOpen(false);
        }
    }
    
    // return focus to the button when we transitioned from !open -> open
    const prevOpen = useRef(addEventDialogOpen);
        useEffect(() => {
            if (prevOpen.current === true && addEventDialogOpen === false) {
                eventAnchorRef.current.focus();
            }
    
            prevOpen.current = addEventDialogOpen;

    }, [addEventDialogOpen]);

    const calndrLink = service => {
        return ;
    }

    return (
        <>
            <ListItem
                key={event.id}
                onClick={() => history.push(`/events/${event.id}`)}
                button
                divider
                >
                {now < DateTime.fromISO(event.starts_at).toSeconds() &&
                    <ListItemAvatar>
                        <Chip
                            className={classes.marginRight}
                            label='Upcoming'
                            color='secondary'
                        />
                    </ListItemAvatar>
                }
                <ListItemText
                    primary={event.name}
                    primaryTypographyProps={{variant: "h6", color: (now > DateTime.fromISO(event.starts_at).toSeconds()) ? 'textSecondary' : 'secondary'}}
                    secondary={DateTime.fromISO(event.starts_at).toLocaleString(DateTime.DATETIME_MED)}
                    secondaryTypographyProps={{variant: "body1"}}
                />
                <ListItemSecondaryAction >
                    <Tooltip title="Add to Calendar">
                        <IconButton
                            ref={eventAnchorRef}
                            aria-controls={addEventDialogOpen ? `add-event-list-${event.id}` : undefined}
                            aria-label="edit"
                            onClick={handleAddEventToggle}
                            aria-haspopup="true"
                            >
                            <CalendarIcon/>
                        </IconButton>
                    </Tooltip>
                </ListItemSecondaryAction>
            </ListItem>
            <Popper open={addEventDialogOpen} anchorEl={eventAnchorRef.current} role={undefined} transition>
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom' }}
                        >
                        <Paper>
                            <ClickAwayListener onClickAway={handleAddEventClose}>
                                <MenuList autoFocusItem={addEventDialogOpen} id={`add-event-list-${event.id}`} onKeyDown={handleListKeyDown}>
                                    <ListItemLink service="apple" event={event}>
                                        <ListItemIcon>
                                            <AppleIcon viewBox="0 0 36 36"/>
                                        </ListItemIcon>
                                        <ListItemText primary="Apple" />
                                    </ListItemLink>
                                    <ListItemLink service="google" event={event}>
                                        <ListItemIcon>
                                            <GoogleIcon viewBox="0 0 36 36"/>
                                        </ListItemIcon>
                                        <ListItemText primary="Google" />
                                    </ListItemLink>
                                    <ListItemLink service="office365" event={event}>
                                        <ListItemIcon>
                                            <Office365Icon viewBox="0 0 36 36"/>
                                        </ListItemIcon>
                                        <ListItemText primary="Office 365" />
                                    </ListItemLink>
                                    <ListItemLink service="outlook" event={event}>
                                        <ListItemIcon>
                                            <OutlookIcon viewBox="0 0 36 36"/>
                                        </ListItemIcon>
                                        <ListItemText primary="Outlook" />
                                    </ListItemLink>
                                    <ListItemLink service="outlookcom" event={event}>
                                        <ListItemIcon>
                                            <OutlookComIcon viewBox="0 0 36 36"/>
                                        </ListItemIcon>
                                        <ListItemText primary="Outlook.com" />
                                    </ListItemLink>
                                    <ListItemLink service="yahoo" event={event}>
                                        <ListItemIcon>
                                            <YahooIcon viewBox="0 0 36 36"/>
                                        </ListItemIcon>
                                        <ListItemText primary="Yahoo" />
                                    </ListItemLink>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    )
}

export default function Events(props) {

    const classes = useStyles();
    const api = useApi();

    const [ data, setData ] = useState([]);
    const [ events, setEvents ] = useState([]);

    const [ loading, setLoading ] = useState(false);

    const [ currentView, setCurrentView ] = useState('Month');
    const [ viewType, setViewType ] = useState('list');
    
    /// Scheduler Testing

    const handleTimeChange = (currentViewName) => {
        setCurrentView(currentViewName && currentViewName.name)
    }

    useEffect(() => {
        handleTimeChange();
    }, [currentView])

    const commitChanges = ({ added, changed, deleted }) => {

        let newData;

        if (added) {
            const startingAddedId = data.length > 0 ? data[data.length - 1].id + 1 : 0;
            newData = [...data, { id: startingAddedId, ...added }];
        };

        if (changed) {
            newData = data.map(appointment => (
            changed[appointment.id] ? { ...appointment, ...changed[appointment.id] } : appointment));
        };

        if (deleted !== undefined) {
            newData = data.filter(appointment => appointment.id !== deleted);
        };

        setData(newData);
    }

    const getData = (input) => {

        let appointments = []

        input.forEach(event => {

            let newAppointment = {};

            newAppointment.id = event.id;
            newAppointment.title = event.name;
            newAppointment.startDate = event.starts_at;
            newAppointment.endDate = event.ends_at;
            newAppointment.description = event.short_description;

            appointments.push(newAppointment)
        })

        setData(appointments);
    }

    const getEvents = () => {
        setLoading(true);

        api.get(`/event`)
            .then(res => {
                getData(res.events)
                setEvents(res.events)
            }).finally(() => setLoading(false));
    }

    useEffect(() => {
        getEvents();
    }, []);

    return (
        <div className={classes.container}>
            <Loading isLoading={loading} />
            <Paper className={classes.paper}>
                <Grid container justify="space-between" alignItems="center" spacing={3}>
                    <Grid item>
                        <Typography variant="h4" component="h2">Events</Typography>
                    </Grid>
                    <Grid item>
                        <Tooltip title="List View">
                            <IconButton
                                onClick={() => setViewType('list')}
                                color={viewType === 'list' ? 'secondary' : 'default'}
                                className={classes.marginLeft}
                                >
                                <ListIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Calendar View">
                            <IconButton
                                onClick={() => setViewType('calendar')}
                                color={viewType === 'calendar' ? 'secondary' : 'default'}
                                className={classes.marginRight}
                                >
                                <CalendarIcon/>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                {viewType === 'calendar' &&
                    <Scheduler data={data}>
                        <ViewState
                            currentViewName={currentView}
                            onCurrentViewNameChange={handleTimeChange}
                            defaultCurrentDate={new Date()}
                        />
                        <MonthView dayScaleCellComponent={DayScaleCell}/>
                        <WeekView startDayHour={8} />
                        <DayView />
                        <Appointments
                            appointmentComponent={Appointment}
                            appointmentContentComponent={AppointmentContent}
                        />
                        <Toolbar />
                        <DateNavigator />
                        <TodayButton />
                        <ViewSwitcher />
                        <AllDayPanel />
                    </Scheduler>
                }
                {viewType === 'list' &&
                    <div>
                        {(events && events.length) ? (
                            <List>
                                {events.map(event => <AdvancedMenuItem key={event.id} event={event}/>)}
                            </List>
                        ) : (
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="subtitle2"
                                className={classes.empty}
                                >
                                You have no events available at this time.
                            </Typography>
                        )}
                    </div>
                }
            </Paper>
        </div>
    );
}
